import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PhotoAdder } from "./Photos";
import { VoiceButton } from "./VoiceButton";
import { ClassDoneList, HomeworkList } from "./HomeLists";

const ITEM_ICON = { Writing: "✏️", Reading: "📖", Numbers: "🔢", Drawing: "🎨", Activity: "🧩", Rhymes: "🎵" };
const MOODS = [{ k: "happy", e: "😃", l: "Happy" }, { k: "ok", e: "😐", l: "Okay" }, { k: "sad", e: "😢", l: "Sad" }];
const STICKERS = ["🦁", "🐼", "🦄", "🐯", "🐸", "🦋", "🐬", "🌟"];

export const KidHome = ({ day, onSaved }) => {
  const [items, setItems] = useState(day.home.done_items || []);
  const [undone, setUndone] = useState(day.home.class_undone || []);
  const [hwDone, setHwDone] = useState(day.home.hw_done || []);
  const [mood, setMood] = useState(day.home.mood || null);
  const [note, setNote] = useState(day.home.note || "");
  const [busy, setBusy] = useState(false);
  const [sticker, setSticker] = useState(null);
  useEffect(() => { setItems(day.home.done_items || []); setUndone(day.home.class_undone || []); setHwDone(day.home.hw_done || []); setMood(day.home.mood || null); setNote(day.home.note || ""); }, [day.date]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/student/diary/home", { date: day.date, done_items: items, class_undone: undone, hw_done: hwDone, mood, note });
      setSticker(STICKERS[Math.floor(Math.random() * STICKERS.length)] + (data.streak > 1 ? ` ${data.streak} days in a row!` : ""));
      setTimeout(() => setSticker(null), 2600);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5 rounded-3xl bg-gradient-to-b from-yellow-50 to-sky-50 p-4 ring-1 ring-yellow-200" data-testid="kid-home">
      <ClassDoneList items={day.class_items} undone={undone} setUndone={setUndone} kid />
      <div>
        <p className="mb-2 text-lg font-extrabold text-slate-800">What did you do today?</p>
        <div className="grid grid-cols-3 gap-3">
          {day.kid_items.map((it) => (
            <button key={it} type="button" onClick={() => toggle(items, setItems, it)} data-testid={`kid-item-${it}`}
              className={`flex flex-col items-center rounded-2xl p-3 text-sm font-bold transition-transform active:scale-95 ${items.includes(it) ? "bg-emerald-500 text-white shadow-lg scale-105" : "bg-white text-slate-700 shadow"}`}>
              <span className="text-4xl">{ITEM_ICON[it]}</span>{it}
            </button>
          ))}
        </div>
      </div>
      <HomeworkList items={day.hw_items} done={hwDone} setDone={setHwDone} kid />
      <div>
        <p className="mb-2 text-lg font-extrabold text-slate-800">How was today?</p>
        <div className="flex gap-3">
          {MOODS.map((m) => (
            <button key={m.k} type="button" onClick={() => setMood(m.k)} data-testid={`kid-mood-${m.k}`}
              className={`flex flex-1 flex-col items-center rounded-2xl p-2 text-sm font-bold ${mood === m.k ? "bg-amber-400 text-amber-950 shadow-lg scale-105" : "bg-white text-slate-600 shadow"}`}>
              <span className="text-4xl">{m.e}</span>{m.l}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-2 text-lg font-extrabold text-slate-800">✍️ Extra work I did</p>
        <div className="flex items-start gap-2">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} placeholder="e.g. I wrote ABC two times"
            className="flex-1 rounded-2xl border-2 border-yellow-200 bg-white p-3 text-base" data-testid="kid-note" />
          <VoiceButton onText={(t) => setNote((x) => (x ? `${x} ${t}` : t))} testid="kid-voice" url="/student/diary/voice" />
        </div>
      </div>
      <div>
        <p className="mb-2 text-lg font-extrabold text-slate-800">Show your notebook 📸</p>
        <PhotoAdder date={day.date} photos={day.photos} onChanged={onSaved} big />
      </div>
      <button type="button" onClick={save} disabled={busy} data-testid="kid-save"
        className="w-full rounded-2xl bg-emerald-600 py-4 text-xl font-extrabold text-white shadow-lg active:scale-95">
        {busy ? <Loader2 className="mx-auto h-6 w-6 animate-spin" /> : "I'm done! 🎉"}
      </button>
      {sticker && <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-testid="kid-sticker">
        <div className="animate-bounce rounded-3xl bg-white px-8 py-6 text-center shadow-2xl ring-4 ring-yellow-300">
          <p className="text-7xl">{sticker.split(" ")[0]}</p><p className="mt-2 text-xl font-extrabold text-emerald-700">Great job! {sticker.split(" ").slice(1).join(" ")}</p>
        </div>
      </div>}
    </div>
  );
};
